import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import useFetchWithParams from '../../services/useFetchWithParams.js'
import PostData from '../../services/PostData.js';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { RECIPE_UOM_OPTIONS, itemUomOptionsForRecipe } from '../Helpers/UoMMenuOptions.js';
import useFetch from '../../services/useFetch.js'
import IngredientStockPanel from './IngredientStockPanel.jsx';
import { tokens } from '../../theme.js';

export default function EditRawIngredientInfo({ ingredientId, vendorId, close, tab }) {
    const [reloadKey, setReloadKey] = useState(0);
    const { data: ingredientInfo, load: ingredientEditLoad } = useFetchWithParams(
        'api/ingredient-data',
        { ingredientId, vendorId },
        reloadKey,
    );
    const [ingredientData, setIngredientData] = useState(null);
    const [isFocused, setIsFocused] = useState(false);
    const [processingEdit, setProcessingEdit] = useState(false);
    const [itemUoMOptions, setItemUoMOptions] = useState([]);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        if (ingredientInfo !== null) {
            setIngredientData(ingredientInfo);
            setItemUoMOptions(
                itemUomOptionsForRecipe(ingredientInfo.recipeUoM, ingredientInfo.parUoM),
            );
            if (ingredientInfo.singleItemSize) setIsFocused(true);
        }
    }, [ingredientInfo]);

    const [menuCategories, setMenuCategories] = useState([
        'Coffee', 'Dairy', 'Disposables', 'Dry Goods', 'Tea', 'Meat', 'Produce',
        'Bread', 'Chemicals/Cleaning', 'Retail', 'Other', 'Packaging',
    ]);

    const { data: serverMenuCategories } = useFetch('api/menuCategories', false);
    useEffect(() => {
        if (serverMenuCategories) setMenuCategories(serverMenuCategories);
    }, [serverMenuCategories]);

    const closePop = () => close(false);

    const recipeUoMOptions = (() => {
        const cur = String(ingredientData?.recipeUoM || '').trim();
        if (cur && !RECIPE_UOM_OPTIONS.includes(cur)) return [cur, ...RECIPE_UOM_OPTIONS];
        return RECIPE_UOM_OPTIONS;
    })();

    const handleInput = ({ target }, fieldName) => {
        let value;
        switch (fieldName) {
            case 'parStock':
            case 'singleItemSize':
            case 'newStock':
            case 'usedStock':
                value = Number(target.value);
                break;
            case 'recipeUoM':
                value = target.value;
                setItemUoMOptions(itemUomOptionsForRecipe(value, ingredientData?.parUoM));
                break;
            case 'parUoM':
                value = target.value;
                break;
            default:
                value = target.value;
                break;
        }
        setIngredientData((curr) => {
            const next = { ...curr, [fieldName]: value };
            if (fieldName === 'recipeUoM') {
                const allowed = itemUomOptionsForRecipe(value, curr.parUoM);
                if (curr.parUoM && !allowed.includes(curr.parUoM)) {
                    next.parUoM = allowed[0] || '';
                }
            }
            if (curr.reusable && (fieldName === 'newStock' || fieldName === 'usedStock')) {
                next.parStock = (Number(fieldName === 'newStock' ? value : curr.newStock) || 0)
                    + (Number(fieldName === 'usedStock' ? value : curr.usedStock) || 0);
            }
            if (curr.reusable && fieldName === 'parStock') {
                const used = Number(curr.usedStock) || 0;
                next.newStock = Math.max((Number(value) || 0) - used, 0);
            }
            return next;
        });
    };

    const handleSubmit = async () => {
        setProcessingEdit(true);
        const { success, error } = await PostData(ingredientData, 'api/edit-raw-ingredient', false);
        setProcessingEdit(false);
        if (success) close('parDetails');
        else if (error) close('ingredient failure');
    };

    const handleBlur = () => {
        if (!ingredientData?.singleItemSize) setIsFocused(false);
    };

    if ((ingredientEditLoad && !ingredientData) || processingEdit) {
        return (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: '80vh', sm: '70vh' }, minHeight: 420 }}>
            <Box
                sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 2,
                    pt: 2,
                    pb: 0,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: "'Fraunces', 'Figtree', serif",
                            fontWeight: 650,
                            fontSize: 20,
                            lineHeight: 1.2,
                        }}
                    >
                        {ingredientData?.ingredient || 'Ingredient'}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mt: 0.25 }}>
                        Edit details · stock history · breakage
                    </Typography>
                </Box>
                <button type="button" className="blank-bttn" aria-label="Close" onClick={closePop}>
                    <CloseIcon />
                </button>
            </Box>

            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ px: 2, borderBottom: `1px solid ${tokens.border}`, minHeight: 40 }}
            >
                <Tab label="Details" sx={{ minHeight: 40, textTransform: 'none' }} />
                <Tab label="Stock & history" sx={{ minHeight: 40, textTransform: 'none' }} />
            </Tabs>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
                {activeTab === 0 && ingredientData && (
                    <Box>
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                label="Raw ingredient"
                                size="small"
                                fullWidth
                                value={ingredientData.ingredient || ''}
                                onChange={(e) => handleInput(e, 'ingredient')}
                            />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                label="Supplier item ID"
                                size="small"
                                fullWidth
                                value={ingredientData.vendorIngredientID || ''}
                                onChange={(e) => handleInput(e, 'vendorIngredientID')}
                            />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                label="Store item name"
                                size="small"
                                fullWidth
                                value={ingredientData.storeName || ''}
                                onChange={(e) => handleInput(e, 'storeName')}
                            />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <Autocomplete
                                size="small"
                                options={menuCategories || []}
                                value={ingredientData.category || ''}
                                onChange={(e, val) => setIngredientData({ ...ingredientData, category: val })}
                                renderInput={(params) => (
                                    <TextField {...params} label="Category" placeholder="Coffee, Packaging…" />
                                )}
                            />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                label="Item packaging"
                                size="small"
                                fullWidth
                                placeholder="Bag, Cup, Carton"
                                value={ingredientData.parPackaging || ''}
                                onChange={(e) => handleInput(e, 'parPackaging')}
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <TextField
                                label="Stock remaining"
                                size="small"
                                type="number"
                                fullWidth
                                value={ingredientData.parStock != null ? Number(ingredientData.parStock) : ''}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {ingredientData.parPackaging || ''}
                                        </InputAdornment>
                                    ),
                                }}
                                onChange={(e) => handleInput(e, 'parStock')}
                                helperText={ingredientData.reusable ? 'Total on hand. For reusable items, split this into New / Used below.' : undefined}
                            />
                        </Box>

                        {ingredientData.reusable ? (
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                <TextField
                                    label="New"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    value={ingredientData.newStock ?? 0}
                                    onChange={(e) => handleInput(e, 'newStock')}
                                    helperText="Unused / purchased"
                                />
                                <TextField
                                    label="Used"
                                    size="small"
                                    type="number"
                                    fullWidth
                                    value={ingredientData.usedStock ?? 0}
                                    onChange={(e) => handleInput(e, 'usedStock')}
                                    helperText="Washed / reusable pool"
                                />
                            </Box>
                        ) : null}

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <TextField
                                select
                                label="Recipe UoM"
                                size="small"
                                sx={{ minWidth: 120, flex: 1 }}
                                value={ingredientData.recipeUoM || ''}
                                onChange={(e) => handleInput(e, 'recipeUoM')}
                            >
                                {recipeUoMOptions.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                label="Item UoM"
                                size="small"
                                sx={{ minWidth: 120, flex: 1 }}
                                value={ingredientData.parUoM || ''}
                                onChange={(e) => handleInput(e, 'parUoM')}
                            >
                                {itemUoMOptions.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Item size"
                                size="small"
                                sx={{ minWidth: 120, flex: 1 }}
                                onFocus={() => setIsFocused(true)}
                                onBlur={handleBlur}
                                InputProps={{
                                    endAdornment: isFocused && ingredientData.parUoM ? (
                                        <InputAdornment position="end">{ingredientData.parUoM}</InputAdornment>
                                    ) : null,
                                }}
                                value={ingredientData.singleItemSize || ''}
                                onChange={(e) => handleInput(e, 'singleItemSize')}
                            />
                        </Box>
                    </Box>
                )}

                {activeTab === 1 && (
                    <IngredientStockPanel
                        ingredientId={ingredientId}
                        ingredientData={ingredientData}
                        onIngredientUpdated={() => {
                            setReloadKey((k) => k + 1);
                            // Also nudge parent list refresh path when closing later
                        }}
                    />
                )}
            </Box>

            {activeTab === 0 && (
                <Box
                    sx={{
                        flexShrink: 0,
                        borderTop: `1px solid ${tokens.border}`,
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <button className="submit" type="button" style={{ width: '80%' }} onClick={handleSubmit}>
                        Save details
                    </button>
                </Box>
            )}
        </Box>
    );
}
